import React, { useRef, useEffect, useState } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'motion/react';
import { cn } from '@/src/infrastructure/lib/utils';

export interface PanoramaProps {
  children?: React.ReactNode;
  imageUrl?: string;
  gyroscope?: boolean;
  lightShafts?: boolean;
  className?: string;
}

export function Panorama({
  children,
  imageUrl,
  gyroscope = false,
  lightShafts = false,
  className,
}: PanoramaProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const shouldReduceMotion = useReducedMotion();
  const [gyroOffset, setGyroOffset] = useState({ x: 0, y: 0 });

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  // Smooth scroll-driven horizontal pan across the panoramic width
  const panX = useTransform(scrollYProgress, [0, 1], ['-8%', '8%']);

  useEffect(() => {
    if (!gyroscope || shouldReduceMotion || typeof window === 'undefined') return;

    const handleOrientation = (e: DeviceOrientationEvent) => {
      if (e.gamma !== null && e.beta !== null) {
        // Clamp tilt values to reasonable range
        const x = Math.max(-15, Math.min(15, e.gamma)) * 1.5;
        const y = Math.max(-15, Math.min(15, e.beta - 45)) * 1.5;
        setGyroOffset({ x, y });
      }
    };

    if (typeof (DeviceOrientationEvent as unknown as { requestPermission?: () => Promise<string> }).requestPermission === 'function') {
      // Handled on user interaction if needed
    } else {
      window.addEventListener('deviceorientation', handleOrientation);
    }

    return () => {
      window.removeEventListener('deviceorientation', handleOrientation);
    };
  }, [gyroscope, shouldReduceMotion]);

  return (
    <div
      ref={containerRef}
      className={cn('relative w-full h-full overflow-hidden isolate z-0', className)}
    >
      {/* Panned Panoramic Background */}
      {imageUrl && (
        <motion.div
          style={{
            x: shouldReduceMotion ? 0 : panX,
            translateX: `${gyroOffset.x}px`,
            translateY: `${gyroOffset.y}px`,
          }}
          className="absolute -top-[10%] -left-[15%] w-[130%] h-[120%] pointer-events-none"
        >
          <img
            src={imageUrl}
            alt=""
            className="w-full h-full object-cover rounded-none"
          />
        </motion.div>
      )}

      {/* Merged Light Shafts Overlay (from Compass) */}
      {lightShafts && (
        <div
          className="absolute inset-0 pointer-events-none z-10 opacity-40 mix-blend-screen animate-pulse"
          style={{
            background:
              'radial-gradient(ellipse at 70% 10%, rgba(255,215,0,0.45) 0%, rgba(255,180,50,0.15) 45%, transparent 75%), linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 60%)',
          }}
          aria-hidden
        />
      )}

      {/* Content wrapper */}
      <div className="relative z-20 w-full h-full flex items-center justify-center">
        {children}
      </div>
    </div>
  );
}
